/**
 * API Client for making HTTP requests with built-in error handling
 * Provides a consistent interface for all API calls with automatic error checking
 */

import type {
  ApiErrorResponse,
  AdminUsersListResponse,
  AdminUserDetailResponse,
  AdminUserPlaylistsResponse,
  AdminResetPasswordResponse,
  AdminBlockUserRequest,
  AdminBlockUserResponse,
  AdminUpdateSubscriptionRequest,
  AdminUpdateSubscriptionResponse,
} from '@maso-dj/shared-types';
import { logger } from '@maso-dj/shared-types';

export interface ApiRequestOptions extends RequestInit {
  /** Optional description of the request for error logging */
  description?: string;
  /** Whether to suppress error logging (default: false) */
  suppressErrorLog?: boolean;
}

/**
 * Success response from ApiClient - the API response with HTTP status added
 */
export type ApiClientSuccessResponse<T> = T & {
  httpStatus: number;
};

/**
 * Error response from ApiClient - API error with HTTP status added
 */
export type ApiClientErrorResponse = ApiErrorResponse & {
  httpStatus: number;
};

/**
 * Response from ApiClient methods
 * Either the success response T with httpStatus, or an error response with httpStatus
 */
export type ApiClientResponse<T> = ApiClientSuccessResponse<T> | ApiClientErrorResponse;

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl;
  }

  /**
   * Make a GET request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Make a POST request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async post<T = any>(
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.requestWithBody<T>('POST', endpoint, body, options);
  }

  /**
   * Make a PUT request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async put<T = any>(
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.requestWithBody<T>('PUT', endpoint, body, options);
  }

  /**
   * Make a DELETE request
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Helper method for POST/PUT requests with JSON body
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async requestWithBody<T = any>(
    method: 'POST' | 'PUT',
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  /**
   * Core request method with error handling
   * Returns the actual API response with HTTP status added as httpStatus field
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected async request<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    const { description, suppressErrorLog, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;
    const requestDescription = description || `${options.method || 'GET'} ${endpoint}`;

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        // CRITICAL: Send cookies with cross-origin requests (for session persistence)
        // SECURITY NOTE: This requires proper CSRF protection on the backend:
        //   1. SameSite=Strict/Lax cookies (preferred for modern browsers)
        //   2. CSRF tokens for additional security (can be added below if needed)
        //   3. Origin/Referer header validation on backend
        // See: https://cheatsheetseries.owasp.org/cheatsheets/Cross-Site_Request_Forgery_Prevention_Cheat_Sheet.html
        credentials: 'include',

        // Optional CSRF token support (uncomment if backend implements CSRF tokens):
        // headers: {
        //   ...fetchOptions.headers,
        //   'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        // },
      });
      const httpStatus = response.status;

      // Try to parse JSON response
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          const jsonData = await response.json();

          // Check if response is OK (status 200-299)
          if (!response.ok) {
            if (!suppressErrorLog) {
              logger.error(
                `API request failed: ${requestDescription}`,
                `Status: ${httpStatus}`,
                jsonData
              );
            }

            // Return error response with status
            return {
              success: false,
              message: jsonData.message || jsonData.error || `Request failed with status ${httpStatus}`,
              error: jsonData.error,
              httpStatus: httpStatus,
            } as ApiClientErrorResponse as ApiClientResponse<T>;
          }

          // Success - return the API response with status added
          return {
            ...jsonData,
            httpStatus: httpStatus,
          } as ApiClientResponse<T>;

        } catch (jsonError) {
          if (!suppressErrorLog) {
            logger.error(`Failed to parse JSON response for ${requestDescription}:`, jsonError);
          }
          return {
            success: false,
            message: 'Failed to parse response',
            error: 'JSON parse error',
            httpStatus: httpStatus,
          } as ApiClientErrorResponse as ApiClientResponse<T>;
        }
      }

      // No JSON content - return success/failure based on HTTP status
      if (!response.ok) {
        if (!suppressErrorLog) {
          logger.error(
            `API request failed: ${requestDescription}`,
            `Status: ${httpStatus}`,
            response.statusText
          );
        }
        return {
          success: false,
          message: response.statusText || `Request failed with status ${httpStatus}`,
          httpStatus: httpStatus,
        } as ApiClientErrorResponse as ApiClientResponse<T>;
      }

      return {
        success: true,
        httpStatus: httpStatus,
      } as unknown as ApiClientResponse<T>;

    } catch (error) {
      // Network error or fetch failed
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (!suppressErrorLog) {
        logger.error(`Network error for ${requestDescription}:`, error);
      }

      return {
        success: false,
        message: errorMessage,
        error: errorMessage,
        httpStatus: 0, // 0 indicates network error
      } as ApiClientErrorResponse as ApiClientResponse<T>;
    }
  }
}

/**
 * Create a default API client instance
 */
export const apiClient = new ApiClient();

/**
 * Create an API client for a specific base URL
 */
export function createApiClient(baseUrl: string): ApiClient {
  return new ApiClient(baseUrl);
}

/**
 * Authenticated API Client that automatically adds Authorization header
 * Requires auth composable to be available (must be used within Vue component context)
 */
export class AuthenticatedApiClient extends ApiClient {
  private getToken: (() => Promise<string | null>) | null = null;

  constructor(baseUrl: string = '') {
    super(baseUrl);
  }

  /**
   * Set the token provider function
   * This should be called with auth.getToken from useAuth composable
   */
  setTokenProvider(getToken: () => Promise<string | null>): void {
    this.getToken = getToken;
  }

  /**
   * Override HTTP methods to add Authorization header
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async get<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.requestWithAuth<T>(endpoint, { ...options, method: 'GET' });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async post<T = any>(
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    const authOptions = await this.addAuthHeader(options);
    return super.requestWithBody<T>('POST', endpoint, body, authOptions);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async put<T = any>(
    endpoint: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    body?: any,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    const authOptions = await this.addAuthHeader(options);
    return super.requestWithBody<T>('PUT', endpoint, body, authOptions);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async delete<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    return this.requestWithAuth<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // =============================================================================
  // Admin API Methods
  // =============================================================================

  /**
   * GET /api/admin/users
   * List all users with pagination
   */
  async getAdminUsers(
    page = 0,
    perPage = 50
  ): Promise<ApiClientResponse<AdminUsersListResponse>> {
    return this.get<AdminUsersListResponse>(
      `/api/admin/users?page=${page}&perPage=${perPage}`,
      { description: 'Get admin users list' }
    );
  }

  /**
   * GET /api/admin/users/:userId
   * Get detailed user information including activity history
   */
  async getAdminUserDetail(userId: string): Promise<ApiClientResponse<AdminUserDetailResponse>> {
    return this.get<AdminUserDetailResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}`,
      { description: 'Get admin user detail' }
    );
  }

  /**
   * GET /api/admin/users/:userId/playlists
   * Get user's playlists
   */
  async getAdminUserPlaylists(
    userId: string
  ): Promise<ApiClientResponse<AdminUserPlaylistsResponse>> {
    return this.get<AdminUserPlaylistsResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/playlists`,
      { description: 'Get admin user playlists' }
    );
  }

  /**
   * POST /api/admin/users/:userId/reset-password
   * Send password reset email
   */
  async resetUserPassword(
    userId: string
  ): Promise<ApiClientResponse<AdminResetPasswordResponse>> {
    return this.post<AdminResetPasswordResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/reset-password`,
      undefined,
      { description: 'Reset user password' }
    );
  }

  /**
   * PUT /api/admin/users/:userId/block
   * Block or unblock a user
   */
  async blockUser(
    userId: string,
    blocked: boolean,
    reason?: string
  ): Promise<ApiClientResponse<AdminBlockUserResponse>> {
    const body: AdminBlockUserRequest = { blocked, reason };
    return this.put<AdminBlockUserResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/block`,
      body,
      { description: `${blocked ? 'Block' : 'Unblock'} user` }
    );
  }

  /**
   * PUT /api/admin/users/:userId/subscription
   * Manually update user subscription
   */
  async updateUserSubscription(
    userId: string,
    updates: AdminUpdateSubscriptionRequest
  ): Promise<ApiClientResponse<AdminUpdateSubscriptionResponse>> {
    return this.put<AdminUpdateSubscriptionResponse>(
      `/api/admin/users/${encodeURIComponent(userId)}/subscription`,
      updates,
      { description: 'Update user subscription' }
    );
  }

  /**
   * POST /api/admin/users/:userId/grant-trial
   * Grant or extend trial period by 30 days
   */
  async grantTrial(
    userId: string
  ): Promise<ApiClientResponse<{ success: boolean; message: string; subscription: unknown }>> {
    return this.post(
      `/api/admin/users/${encodeURIComponent(userId)}/grant-trial`,
      undefined,
      { description: 'Grant trial to user' }
    );
  }

  /**
   * POST /api/admin/entitlements/generate
   * Generate an entitlement token for any user (admin only)
   */
  async generateAdminEntitlement(
    email: string,
    expiresAt: string,
    planType: string
  ): Promise<ApiClientResponse<{ success: boolean; token: string; data: Record<string, unknown> }>> {
    return this.post(
      '/api/admin/entitlements/generate',
      { email, expiresAt, planType },
      { description: 'Generate admin entitlement' }
    );
  }

  /**
   * GET /api/admin/health
   * Get system health check including circuit breaker status
   */
  async getAdminHealth(): Promise<ApiClientResponse<{ success: boolean; health: unknown; circuitBreakers: unknown }>> {
    return this.get(
      '/api/admin/health',
      { description: 'Get system health check' }
    );
  }

  /**
   * POST /api/admin/circuit-breaker/reset
   * Reset circuit breaker state
   */
  async resetCircuitBreaker(circuitName: string): Promise<ApiClientResponse<{ success: boolean; message: string }>> {
    return this.post(
      '/api/admin/circuit-breaker/reset',
      { circuitName },
      { description: 'Reset circuit breaker' }
    );
  }

  /**
   * Add Authorization header to options if token provider is set
   */
  private async addAuthHeader(options: ApiRequestOptions): Promise<ApiRequestOptions> {
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        return {
          ...options,
          headers: {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          },
        };
      }
    }
    return options;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async requestWithAuth<T = any>(
    endpoint: string,
    options: ApiRequestOptions = {}
  ): Promise<ApiClientResponse<T>> {
    // Add Authorization header if token provider is set
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    }

    // Call parent request method
    return super['request']<T>(endpoint, options);
  }
}

/**
 * Create an authenticated API client for a specific base URL
 * Note: You must call setTokenProvider() before making requests
 */
export function createAuthenticatedApiClient(baseUrl: string = ''): AuthenticatedApiClient {
  return new AuthenticatedApiClient(baseUrl);
}

/**
 * Default authenticated API client instance
 * Note: You must call setTokenProvider() before making requests
 */
export const authenticatedApiClient = new AuthenticatedApiClient();
